import { useCallback, useMemo } from "react";
import { I18nMarkup, useI18nContext } from "@hopper-b2b/i18n";
import { type IMonthBucket, TripCategory } from "@hopper-b2b/types";
import {
  DesktopPopupModal,
  IconComponent,
  IconName,
  MobilePopoverCard,
} from "@hopper-b2b/ui";
import { ActionButton } from "@hopper-b2b/ui-core";
import { Button, Divider, Typography } from "@material-ui/core";
import clsx from "clsx";
import dayjs from "dayjs";
import styles from "./CalendarPicker.module.scss";
import {
  MonthAndDatePicker,
  MonthAndDatePickerType,
} from "./MonthAndDatePicker";

export interface CalendarTrackingProperties {
  origin: string;
  destination: string;
  tripCategory: TripCategory;
}

export interface ICalendarPickerProps {
  open?: boolean;
  departureDate: Date | null;
  returnDate: Date | null;
  setDepartureDate: (value: Date | null) => void;
  setReturnDate: (value: Date | null) => void;
  closePopup?: () => void;
  disabled: boolean;
  tripCategory: TripCategory;
  focusedMonthIndex?: number;
  onComplete?: () => void;
  isHistoryPushDisabled?: boolean;
  isMobile?: boolean;
  modal?: boolean;
  trackingProperties: CalendarTrackingProperties;
  prices?: string[];
  months?: IMonthBucket[];
  headerTitle: string;
  startDateLabel?: string;
  endDateLabel?: string;
  className?: string;
  paperClassName?: string;
  minAllowedDate?: Date;
  maxAllowedDate?: Date;
  startDateIcon?: React.ReactElement;
  endDateIcon?: React.ReactElement;
  isDateUnavailable?: (date: Date, selectingReturnDate: boolean) => boolean;
  dateHasPrice?: (date: Date, selectingReturnDate: boolean) => string | null;
  showResetDatesBtn?: boolean;
  showCalendarPricesLabel?: boolean;
  desktopCloseIcon?: JSX.Element;
  hideWeekdayLabels?: boolean;
  currency?: string;
}

export const CalendarPicker = ({
  open,
  closePopup,
  disabled,
  tripCategory,
  departureDate,
  returnDate,
  onComplete,
  isMobile = false,
  modal = false,
  setDepartureDate,
  setReturnDate,
  prices,
  months,
  startDateLabel,
  endDateLabel,
  startDateIcon,
  endDateIcon,
  headerTitle,
  className,
  paperClassName,
  minAllowedDate,
  maxAllowedDate,
  isDateUnavailable,
  dateHasPrice,
  showResetDatesBtn,
  showCalendarPricesLabel,
  currency,
  desktopCloseIcon,
  hideWeekdayLabels,
}: ICalendarPickerProps) => {
  const { t } = useI18nContext();
  const focusedMonthIndex = departureDate
    ? dayjs(departureDate).diff(dayjs(), "month")
    : 0;

  const calendarClassName = useMemo(
    () => (tripCategory === TripCategory.ONE_WAY ? "one-way" : "roundtrip"),
    [tripCategory]
  );

  const onDatesSelect = useCallback(() => {
    onComplete ? onComplete() : closePopup?.();
  }, [closePopup, onComplete]);

  const readyToSearch = useMemo(() => {
    return tripCategory === TripCategory.ONE_WAY
      ? !!departureDate
      : tripCategory === TripCategory.ROUND_TRIP
      ? !!returnDate && !!departureDate
      : false;
  }, [tripCategory, departureDate, returnDate]);

  const onResetDatesClick = useCallback(() => {
    setDepartureDate(null);
    setReturnDate(null);
  }, [setDepartureDate, setReturnDate]);

  return isMobile ? (
    <MobilePopoverCard
      className={styles["mobile-calendar-picker-root"]}
      fullScreen
      open={open}
      onClose={closePopup}
      paperClassName={paperClassName}
      topRightButton={
        <button className="close-mobile-calendar" onClick={closePopup}>
          {desktopCloseIcon ?? <IconComponent name={IconName.Close} />}
        </button>
      }
      bottomButton={
        readyToSearch ? (
          <>
            {showCalendarPricesLabel && (
              <div className="calendar-prices-label">
                <Typography>
                  <I18nMarkup
                    tKey="searchControl.calendarPricesInLabel"
                    replacements={{ currency }}
                  />
                </Typography>
                <Divider />
              </div>
            )}
            <div className="dates-buttons-container">
              <ActionButton
                className="mobile-calendar-picker-search-button"
                onClick={onDatesSelect}
                message={t("searchControl.calendarPicker")}
                disabled={!readyToSearch}
              />
              {showResetDatesBtn && (
                <Button
                  onClick={onResetDatesClick}
                  className="reset-dates-button"
                  variant="text"
                >
                  {t("searchControl.resetDates")}
                </Button>
              )}
            </div>
          </>
        ) : null
      }
    >
      <MonthAndDatePicker
        tripCategory={tripCategory}
        currency=""
        className={clsx(className, `b2b ${calendarClassName}`, {
          "search-ready": readyToSearch,
          modal,
        })}
        priceTags={prices}
        viewType={MonthAndDatePickerType.Column}
        focusedMonthIndex={focusedMonthIndex}
        setStartDate={setDepartureDate}
        setEndDate={setReturnDate}
        startDate={departureDate}
        endDate={returnDate}
        months={months}
        header={headerTitle}
        startDateLabel={startDateLabel}
        endDateLabel={endDateLabel}
        minAllowedDate={minAllowedDate}
        maxAllowedDate={maxAllowedDate}
        startDateIcon={startDateIcon}
        endDateIcon={endDateIcon}
        isDateUnavailable={isDateUnavailable}
        hideWeekdayLabels={hideWeekdayLabels}
        dateHasPrice={dateHasPrice}
      />
    </MobilePopoverCard>
  ) : (
    <DesktopPopupModal
      open={Boolean(open)}
      onClose={closePopup}
      className={clsx(
        styles["desktop-calendar-picker-popup-root"],
        "desktop-flight-calendar-picker-popup-root",
        "flights-module",
        calendarClassName
      )}
      contentClassName="desktop-calendar-picker-wrapper"
      invisibleBackdrop={false}
      closeIcon={desktopCloseIcon}
    >
      <MonthAndDatePicker
        tripCategory={tripCategory}
        currency=""
        className={clsx(className, `b2b ${calendarClassName}`, {
          "search-ready": readyToSearch,
          modal,
        })}
        priceTags={prices}
        viewType={MonthAndDatePickerType.Horizontal}
        focusedMonthIndex={focusedMonthIndex}
        setStartDate={setDepartureDate}
        setEndDate={setReturnDate}
        startDate={departureDate}
        endDate={returnDate}
        header={headerTitle}
        months={months}
        minAllowedDate={minAllowedDate}
        maxAllowedDate={maxAllowedDate}
        isDateUnavailable={isDateUnavailable}
        dateHasPrice={dateHasPrice}
      />
      <div className="dates-buttons-container">
        <ActionButton
          onClick={onDatesSelect}
          disabled={disabled}
          className="select-dates-button"
          message={t("searchControl.calendarPicker")}
        />
        {showResetDatesBtn && (
          <Button
            onClick={onResetDatesClick}
            className={"reset-dates-button"}
            variant="text"
            disableRipple
          >
            {t("searchControl.resetDates")}
          </Button>
        )}
        {showCalendarPricesLabel && (
          <div className="calendar-prices-label">
            <Typography>
              <I18nMarkup
                tKey="searchControl.calendarPricesInLabel"
                replacements={{ currency }}
              />
            </Typography>
          </div>
        )}
      </div>
    </DesktopPopupModal>
  );
};
